<template>
  <div class="link-editor">
    <h3 class="link-title">{{ heading }} ({{ attachedOptions.length }})</h3>
    <p v-if="!attachedOptions.length" class="link-muted">{{ emptyText }}</p>
    <ul v-else class="link-list">
      <li v-for="option in attachedOptions" :key="option.id" class="link-row">
        <span class="link-text">
          <span class="link-name">{{ option.label }}{{ option.active ? '' : ' (archived)' }}</span>
          <span class="link-sub">{{ option.sub }}</span>
        </span>
        <span v-if="confirmingId === option.id" class="link-actions">
          <span class="link-muted">Detach in Stripe?</span>
          <aha-button size="sm" variant="danger" :disabled="busy" :loading="busy" @click="detach(option.id)">Detach</aha-button>
          <aha-button size="sm" variant="secondary" :disabled="busy" @click="confirmingId = ''">Keep</aha-button>
        </span>
        <aha-button v-else size="sm" variant="secondary" :disabled="busy" @click="confirmingId = option.id">Detach</aha-button>
      </li>
    </ul>

    <div class="link-picker">
      <span class="link-label">{{ pickLabel }}</span>
      <stripe-bridge-multi-picker
        v-model="selected"
        :label="pickerLabel"
        :placeholder="placeholder"
        :options="pickable"
        :attached-ids="attachedIds"
        :errors="errors"
        :disabled="busy"
      ></stripe-bridge-multi-picker>
    </div>
    <aha-button
      variant="primary"
      :disabled="!selected.length || busy"
      :loading="busy"
      @click="attach"
    >
      <span slot="icon"><aha-icon name="system-link" size="16" decorative></aha-icon></span>
      {{ selected.length > 1 ? `Attach ${selected.length}` : 'Attach' }}
    </aha-button>
    <aha-alert v-if="message" :type="message.type" size="small" role="status">{{ message.text }}</aha-alert>
  </div>
</template>

<script>
import nativeAhaElements from './nativeAhaElements'
import StripeBridgeMultiPicker from './StripeBridgeMultiPicker'

export default {
  name: 'StripeBridgeLinkEditor',
  components: { ...nativeAhaElements, StripeBridgeMultiPicker },
  props: {
    heading: { type: String, required: true },
    emptyText: { type: String, required: true },
    pickLabel: { type: String, required: true },
    pickerLabel: { type: String, required: true },
    placeholder: { type: String, default: '' },
    options: { type: Array, required: true },
    attachedIds: { type: Array, required: true },
    busy: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    message: { type: Object, default: null },
  },
  data() {
    return { selected: [], confirmingId: '' }
  },
  computed: {
    attachedOptions() {
      return this.options.filter(o => this.attachedIds.includes(o.id))
    },
    pickable() {
      return this.options.filter(o => o.active)
    },
  },
  watch: {
    attachedIds(next, previous) {
      if (next.length === previous.length && next.every(id => previous.includes(id))) return
      this.selected = this.selected.filter(id => !next.includes(id))
      this.confirmingId = ''
    },
  },
  methods: {
    attach() {
      this.$emit('attach', [...this.selected])
    },
    detach(id) {
      this.$emit('detach', id)
    },
  },
}
</script>

<style lang="scss" scoped>
.link-editor {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-8);
}

.link-title {
  margin: 0;
  font-size: var(--aha-size-default);
  font-weight: 600;
}

.link-label {
  font-size: var(--aha-size-sm);
  font-weight: 600;
}

.link-muted {
  margin: 0;
  font-size: var(--aha-size-sm);
  color: var(--aha-text-secondary);
}

.link-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--aha-border-default);
  border-radius: var(--aha-radius-default);
}

.link-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--aha-space-8);
  padding: var(--aha-space-8) var(--aha-space-12);
  border-bottom: 1px solid var(--aha-border-default);

  &:last-child {
    border-bottom: 0;
  }
}

.link-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.link-name {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.link-sub {
  font-size: var(--aha-size-sm);
  color: var(--aha-text-secondary);
  overflow-wrap: anywhere;
}

.link-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--aha-space-8);
}
</style>
